import { FieldArray } from 'formik';
import Image from 'next/image';
import { FocusEventHandler, useEffect, useState } from 'react';

import styles from './BasicFieldArray.module.css';

export interface FieldArrayProps {
  id: string;
  disabled: boolean;
  label: JSX.Element;
  value: (string | undefined)[] | undefined;
  placeholder: string;
  errors?: string | string[];
  // Formik element that triggers validation when blurred
  onBlur: FocusEventHandler<HTMLInputElement>;
  // Formik element that determines if a user has visited a field
  touched: boolean | undefined;
}

export const BasicFieldArray = ({
  id,
  disabled,
  label,
  value,
  placeholder,
  errors,
  onBlur,
  touched,
}: FieldArrayProps) => {
  const [newListElement, setNewListElement] = useState<string>('');

  const listenToEnterKeypress = (e: KeyboardEvent) => {
    const addButton = document.getElementById(
      `${id}-add-button`
    ) as HTMLButtonElement;
    if (addButton && e.key === 'Enter') {
      e.preventDefault();
      addButton.click();
    }
  };

  useEffect(() => {
    const input = document.getElementById(id);
    if (input) {
      input.addEventListener('keypress', listenToEnterKeypress);
      return () => input.removeEventListener('keypress', listenToEnterKeypress);
    }
  }, [id]);

  return (
    <FieldArray
      name={id}
      render={(arrayHelpers) => {
        return (
          <div className={styles['container']}>
            <form onSubmit={(e) => e.preventDefault()}>
              <label htmlFor={id} className={styles['label']}>
                {label}
              </label>
              <div className={styles['input-wrapper']}>
                <input
                  id={id}
                  aria-label={`${id}-input`}
                  aria-invalid={errors ? true : false}
                  aria-errormessage={`${id}-error-message`}
                  disabled={disabled}
                  placeholder={placeholder}
                  value={newListElement}
                  onChange={(e) => setNewListElement(e.target.value)}
                  onBlur={onBlur}
                  className={styles['input']}
                  style={{
                    borderColor: !touched
                      ? 'var(--ea-black)'
                      : errors
                      ? 'var(--error-color)'
                      : 'var(--ea-blue)',
                  }}
                />

                {touched && errors && (
                  <p id={`${id}-error-message`} className={styles['errors']}>
                    {errors}
                  </p>
                )}
              </div>
            </form>

            <div className={styles['options-enter-container']}>
              <div
                aria-label={`${id}-text-list`}
                className={styles['options-grid']}
              >
                {value?.map((text, index) => {
                  return (
                    <div
                      key={`${id}-${text || ''}-${index}`}
                      className={styles['option-container']}
                    >
                      <p className={styles['option-text']}>{text}</p>
                      <button
                        type="button"
                        onClick={() => arrayHelpers.remove(index)}
                        className={styles['option-x-button']}
                      >
                        <Image
                          src="/icons/CloseBlack.svg"
                          alt="delete"
                          objectFit="contain"
                          height={16}
                          width={16}
                        />
                      </button>
                    </div>
                  );
                })}
              </div>
              <button
                type="button"
                id={`${id}-add-button`}
                aria-label={`${id}-add-button`}
                disabled={disabled}
                onClick={(e) => {
                  e.preventDefault();
                  if (newListElement) {
                    arrayHelpers.push(newListElement);
                    setNewListElement('');
                  }
                }}
                className={styles['enter-button']}
              >
                <p>Press Enter</p>{' '}
                <Image
                  src="/icons/EnterArrowBlack.svg"
                  alt="Press Enter"
                  objectFit="contain"
                  height={6}
                  width={12}
                />
              </button>
            </div>
          </div>
        );
      }}
    />
  );
};
